import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Package, Plus, Pencil, Trash2, BarChart3, Users, TrendingUp, Search, Upload, Image as ImageIcon, X, Camera, Store, Check, AlertTriangle, FileDown, FileUp } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { resolveImageUrl as resolveImg } from "../context/AppSettingsContext";
import BranchMultiSelect from "../components/BranchMultiSelect";
import { Textarea } from "../components/ui/textarea";
import AiHelper from "../components/AiHelper";
import AiImage from "../components/AiImage";

const EMPTY = { name: "", sku: "", price: "", promo_price: "", promo_until: "", category: "", description: "", allergens: [], active: true, images: [], image_thumbs: [], branch_ids: [] };

export default function Products() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [analytics, setAnalytics] = useState(null);
  const [range, setRange] = useState(30);
  const [customersFor, setCustomersFor] = useState(null);
  const [productCustomers, setProductCustomers] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [galleryFor, setGalleryFor] = useState(null); // product for lightbox
  const [branches, setBranches] = useState([]);
  const [filterBranchIds, setFilterBranchIds] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null); // { created, updated, errors }
  const fileRef = React.useRef(null);
  const importRef = React.useRef(null);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/products/import-template", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "template-prodotti.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Template scaricato");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const onImportFile = async (e) => {
    const f = (e.target.files || [])[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".xlsx")) {
      toast.error("Seleziona un file .xlsx");
      e.target.value = "";
      return;
    }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/products/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      const errCount = (data.errors || []).length;
      if (errCount > 0) {
        toast.warning(`Importati ${data.created} nuovi + ${data.updated} aggiornati · ${errCount} righe con errori`);
      } else {
        toast.success(`Importati ${data.created} nuovi · ${data.updated} aggiornati`);
      }
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setImporting(false); e.target.value = ""; }
  };

  const load = async () => {
    const params = filterBranchIds.length > 0 ? { branch_id: filterBranchIds.join(",") } : {};
    const [p, a] = await Promise.all([
      api.get("/products", { params }),
      api.get("/products/analytics", { params: { days: range } }),
    ]);
    setRows(p.data);
    setAnalytics(a.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [range, filterBranchIds.join(",")]);
  useEffect(() => {
    api.get("/branches").then((r) => setBranches(r.data || [])).catch(() => setBranches([]));
  }, []);

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Nome obbligatorio");
    const images = (form.images || []).filter(Boolean);
    const image_thumbs = (form.image_thumbs || []).filter(Boolean);
    const payload = {
      name: form.name.trim(),
      sku: form.sku.trim() || null,
      price: parseFloat(form.price || "0"),
      promo_price: form.promo_price !== "" && form.promo_price != null ? parseFloat(form.promo_price) : null,
      promo_until: form.promo_until || null,
      category: form.category.trim() || null,
      description: (form.description || "").trim() || null,
      allergens: (form.allergens || []).map((a) => a.trim()).filter(Boolean),
      active: form.active,
      images,
      image_thumbs,
      image_url: images[0] || null,
      branch_ids: form.branch_ids || [],
    };
    try {
      if (editing) await api.patch(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success("Prodotto salvato");
      setOpen(false); setEditing(null); setForm(EMPTY);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare il prodotto?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Eliminato"); load();
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const upUrls = [];
      const upThumbs = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("kind", "product");
        const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        upUrls.push(data.url);
        upThumbs.push(data.thumb_url || data.url);
      }
      setForm((prev) => ({
        ...prev,
        images: [...(prev.images || []), ...upUrls],
        image_thumbs: [...(prev.image_thumbs || []), ...upThumbs],
      }));
      toast.success(`${upUrls.length} foto caricate`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const removeImage = (idx) => setForm((prev) => ({
    ...prev,
    images: (prev.images || []).filter((_, i) => i !== idx),
    image_thumbs: (prev.image_thumbs || []).filter((_, i) => i !== idx),
  }));
  const moveImage = (idx, dir) => setForm((prev) => {
    const arr = [...(prev.images || [])];
    const th = [...(prev.image_thumbs || [])];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return prev;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    [th[idx], th[j]] = [th[j], th[idx]];
    return { ...prev, images: arr, image_thumbs: th };
  });

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    const imgs = (p.images && p.images.length) ? p.images : (p.image_url ? [p.image_url] : []);
    const thumbs = p.image_thumbs && p.image_thumbs.length ? p.image_thumbs : imgs;
    setForm({
      name: p.name || "", sku: p.sku || "", price: String(p.price ?? ""),
      promo_price: p.promo_price != null ? String(p.promo_price) : "", promo_until: p.promo_until || "",
      category: p.category || "",
      description: p.description || "",
      allergens: Array.isArray(p.allergens) ? p.allergens : [],
      active: p.active !== false,
      images: imgs,
      image_thumbs: thumbs,
      branch_ids: p.branch_ids || [],
    });
    setOpen(true);
  };

  const openCustomers = async (row) => {
    if (!row.product_id) {
      toast.error("Solo per prodotti registrati in catalogo");
      return;
    }
    setCustomersFor(row);
    try {
      const { data } = await api.get(`/products/${row.product_id}/customers`, { params: { days: 180 } });
      setProductCustomers(data.customers);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const filtered = rows.filter((r) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (r.name || "").toLowerCase().includes(s) ||
           (r.sku || "").toLowerCase().includes(s) ||
           (r.category || "").toLowerCase().includes(s);
  });

  return (
    <div className="space-y-6 animate-fade-up" data-testid="products-page">
      <div className="flex flex-col sm:flex-row justify-between gap-3">
        <div>
          <span className="pill pill-gold"><Package className="h-3 w-3" /> CATALOGO PRODOTTI</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold mt-3">Prodotti del negozio</h1>
          <p className="text-zinc-400 mt-1">Gestisci il menu e analizza gli acquisti dei clienti.</p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-2 flex-wrap">
          {branches.length > 1 && (
            <div className="w-full sm:w-56">
              <BranchMultiSelect
                branches={branches}
                value={filterBranchIds}
                onChange={setFilterBranchIds}
                testId="product-filter-branch"
              />
            </div>
          )}
          <Button
            data-testid="product-download-template"
            variant="outline"
            onClick={downloadTemplate}
            className="border-zinc-800 h-11"
            title="Scarica il file Excel di esempio"
          >
            <FileDown className="h-4 w-4 mr-2" /> Template Excel
          </Button>
          <Button
            data-testid="product-import-excel"
            variant="outline"
            onClick={() => importRef.current?.click()}
            disabled={importing}
            className="border-amber-500/60 text-amber-200 hover:bg-amber-500/10 h-11"
          >
            <FileUp className="h-4 w-4 mr-2" />
            {importing ? "Importazione…" : "Importa Excel"}
          </Button>
          <input
            ref={importRef}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            data-testid="product-import-file-input"
            onChange={onImportFile}
          />
          <Button data-testid="product-new" onClick={openNew} className="bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)] h-11">
            <Plus className="h-4 w-4 mr-2" /> Nuovo prodotto
          </Button>
        </div>
      </div>

      {/* Analytics summary */}
      {analytics && (
        <div className="card-elev p-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <div>
              <h3 className="font-heading font-semibold text-lg flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-amber-300" /> Andamento vendite
              </h3>
              <p className="text-xs text-zinc-500">
                Trend degli ultimi {analytics.range_days} giorni · fatturato prodotti
              </p>
            </div>
            <div className="flex gap-2">
              {[7, 30, 90, 180].map((r) => (
                <Button
                  key={r}
                  size="sm"
                  data-testid={`products-range-${r}`}
                  variant={range === r ? "default" : "outline"}
                  onClick={() => setRange(r)}
                  className={`h-8 ${range === r ? "bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]" : "border-zinc-800"}`}
                >
                  {r}gg
                </Button>
              ))}
            </div>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={120}>
              <LineChart data={analytics.series} data-testid="products-trend-chart">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="label" stroke="#64748B" fontSize={11} />
                <YAxis stroke="#64748B" fontSize={11} />
                <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 10 }} />
                <Line type="monotone" dataKey="revenue" name="€" stroke="#1E40AF" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="units" name="Pz" stroke="#10B981" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-4">
            <h4 className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">
              Top prodotti nel periodo
            </h4>
            {analytics.products.length === 0 ? (
              <div className="text-sm text-zinc-500 py-4">Nessuna vendita di prodotti registrata.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-widest text-zinc-500">
                    <tr className="text-left">
                      <th className="py-2 pr-3">Prodotto</th>
                      <th className="py-2 pr-3">Unità</th>
                      <th className="py-2 pr-3">Fatturato</th>
                      <th className="py-2 pr-3">Ordini</th>
                      <th className="py-2 pr-3">Clienti</th>
                      <th className="py-2 pr-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.products.slice(0, 20).map((r, i) => (
                      <tr key={r.product_id || `f-${i}`} className="border-t border-zinc-900" data-testid={`product-stat-${r.product_id || i}`}>
                        <td className="py-2 pr-3">
                          <div className="font-medium">{r.name}</div>
                          <div className="text-xs text-zinc-500 font-mono">{r.sku || "—"}</div>
                        </td>
                        <td className="py-2 pr-3 font-mono">{r.units}</td>
                        <td className="py-2 pr-3 font-mono text-emerald-300">€ {r.revenue.toFixed(2)}</td>
                        <td className="py-2 pr-3 font-mono">{r.orders}</td>
                        <td className="py-2 pr-3 font-mono text-amber-300">{r.customers}</td>
                        <td className="py-2 pr-3">
                          {r.product_id && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-zinc-800 h-7 px-2"
                              onClick={() => openCustomers(r)}
                              data-testid={`product-customers-${r.product_id}`}
                            >
                              <Users className="h-3 w-3 mr-1" /> Clienti
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Catalog */}
      <div className="card-elev p-4">
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input
            data-testid="products-search"
            placeholder="Cerca per nome, sku, categoria…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 bg-zinc-950 border-zinc-800 h-11"
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-zinc-500">
              <tr className="text-left">
                <th className="py-2 pr-3">Prodotto</th>
                <th className="py-2 pr-3">SKU</th>
                <th className="py-2 pr-3">Categoria</th>
                <th className="py-2 pr-3">Prezzo</th>
                <th className="py-2 pr-3">Stato</th>
                <th className="py-2 pr-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="py-8 text-center text-zinc-500">Nessun prodotto in catalogo</td></tr>
              )}
              {filtered.map((p) => {
                const gallery = (p.images && p.images.length) ? p.images : (p.image_url ? [p.image_url] : []);
                const cover = gallery[0];
                return (
                <tr key={p.id} className="border-t border-zinc-900" data-testid={`product-row-${p.id}`}>
                  <td className="py-3 pr-3 font-medium">
                    <div className="flex items-center gap-3">
                      {cover ? (
                        <button
                          data-testid={`product-gallery-open-${p.id}`}
                          onClick={() => setGalleryFor(p)}
                          className="h-11 w-11 rounded-md overflow-hidden bg-zinc-950 border border-zinc-800 shrink-0 relative group"
                        >
                          <img src={resolveImg(cover)} alt={p.name} className="w-full h-full object-cover" />
                          {gallery.length > 1 && (
                            <span className="absolute bottom-0 right-0 bg-black/70 text-[9px] px-1 rounded-tl text-amber-300 font-mono">
                              +{gallery.length - 1}
                            </span>
                          )}
                        </button>
                      ) : (
                        <div className="h-11 w-11 rounded-md bg-zinc-950 border border-zinc-800 flex items-center justify-center shrink-0">
                          <ImageIcon className="h-4 w-4 text-zinc-600" />
                        </div>
                      )}
                      <span>{p.name}</span>
                    </div>
                  </td>
                  <td className="py-3 pr-3 font-mono text-xs">{p.sku || "—"}</td>
                  <td className="py-3 pr-3 text-zinc-400">{p.category || "—"}</td>
                  <td className="py-3 pr-3 font-mono text-amber-300">€ {(p.price ?? 0).toFixed(2)}{p.promo_price != null && <span className="ml-1 rounded bg-rose-600 text-white px-1.5 text-[10px] font-sans font-bold" data-testid={`product-promo-${p.id}`}>OFFERTA € {Number(p.promo_price).toFixed(2)}</span>}</td>
                  <td className="py-3 pr-3">
                    <span className={`pill ${p.active ? "pill-mint" : "pill-muted"}`}>
                      {p.active ? "Attivo" : "Off"}
                    </span>
                  </td>
                  <td className="py-3 pr-3 text-right">
                    <button data-testid={`product-edit-${p.id}`} onClick={() => openEdit(p)} className="p-2 rounded-md text-zinc-500 hover:text-amber-300">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button data-testid={`product-delete-${p.id}`} onClick={() => remove(p.id)} className="p-2 rounded-md text-zinc-500 hover:text-red-400">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800">
          <DialogHeader><DialogTitle>{editing ? "Modifica prodotto" : "Nuovo prodotto"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-zinc-300 mb-1 inline-block">Nome*</Label>
              <Input data-testid="product-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-950 border-zinc-800" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-300 mb-1 inline-block">SKU</Label>
                <Input data-testid="product-form-sku" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="bg-zinc-950 border-zinc-800 font-mono" />
              </div>
              <div>
                <Label className="text-zinc-300 mb-1 inline-block">Prezzo €</Label>
                <Input data-testid="product-form-price" type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="bg-zinc-950 border-zinc-800" />
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div><Label className="text-amber-300 text-xs mb-1 inline-block">Prezzo in offerta €</Label><Input data-testid="product-form-promo-price" type="number" step="0.01" min="0" value={form.promo_price || ""} onChange={(e) => setForm({ ...form, promo_price: e.target.value })} className="bg-zinc-950 border-amber-700/60" /></div>
                  <div><Label className="text-amber-300 text-xs mb-1 inline-block">Fino al</Label><Input data-testid="product-form-promo-until" type="date" value={form.promo_until || ""} onChange={(e) => setForm({ ...form, promo_until: e.target.value })} className="bg-zinc-950 border-amber-700/60" /></div>
                </div>
              </div>
            </div>
            <div>
              <Label className="text-zinc-300 mb-1 inline-block">Categoria</Label>
              <Input data-testid="product-form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Es. Panini, Bevande…" className="bg-zinc-950 border-zinc-800" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-zinc-300 inline-block">Descrizione</Label>
                <AiHelper
                  preset="product_copy"
                  contextLabel="Suggerisci descrizione + allergeni"
                  hint={`Nome prodotto: ${form.name || "…"} — descrivi ingredienti/base`}
                  defaultPrompt={form.name ? `Prodotto: ${form.name}. Genera descrizione breve e allergeni suggeriti.` : ""}
                  triggerLabel="AI"
                  testid="products-ai-copy"
                  onApply={(v) => {
                    if (typeof v === "object" && v) {
                      setForm((prev) => ({
                        ...prev,
                        description: v.description || prev.description,
                        allergens: Array.isArray(v.suggested_allergens)
                          ? Array.from(new Set([...(prev.allergens || []), ...v.suggested_allergens]))
                          : prev.allergens,
                      }));
                    }
                  }}
                />
              </div>
              <Textarea
                data-testid="product-form-description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
                placeholder="Ingredienti, provenienza, note…"
                className="bg-zinc-950 border-zinc-800"
              />
            </div>
            <div>
              <Label className="text-zinc-300 mb-1 inline-block flex items-center gap-1">
                <AlertTriangle className="h-3 w-3 text-amber-300" /> Allergeni
              </Label>
              <div className="flex flex-wrap gap-1.5 mb-2" data-testid="product-form-allergens-tags">
                {(form.allergens || []).map((a, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-amber-500/15 text-amber-200 border border-amber-500/30">
                    {a}
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, allergens: form.allergens.filter((_, j) => j !== i) })}
                      className="hover:text-red-300"
                      data-testid={`product-form-allergen-remove-${i}`}
                    ><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
              <Input
                data-testid="product-form-allergens-input"
                placeholder="Es. Glutine, Latte, Uova — invio per aggiungere"
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") {
                    e.preventDefault();
                    const v = e.currentTarget.value.trim();
                    if (v && !(form.allergens || []).includes(v)) {
                      setForm({ ...form, allergens: [...(form.allergens || []), v] });
                    }
                    e.currentTarget.value = "";
                  }
                }}
                onBlur={(e) => {
                  const v = e.currentTarget.value.trim();
                  if (v && !(form.allergens || []).includes(v)) {
                    setForm({ ...form, allergens: [...(form.allergens || []), v] });
                  }
                  e.currentTarget.value = "";
                }}
                className="bg-zinc-950 border-zinc-800"
              />
              <p className="text-[11px] text-zinc-500 mt-1">Premi Invio o virgola per confermare ogni allergene.</p>
            </div>

            {/* Gallery */}
            <div>
              <Label className="text-zinc-300 mb-1 inline-block flex items-center gap-1">
                <Camera className="h-3 w-3" /> Photo gallery
              </Label>
              <div className="grid grid-cols-4 gap-2 mb-2">
                {(form.images || []).map((url, idx) => (
                  <div key={idx} className="relative group rounded-lg overflow-hidden border border-zinc-800 bg-zinc-950 aspect-square" data-testid={`product-img-${idx}`}>
                    <img src={resolveImg(url)} alt={`img-${idx}`} className="w-full h-full object-cover" />
                    {idx === 0 && (
                      <span className="absolute top-1 left-1 pill pill-gold text-[9px] backdrop-blur-md bg-black/50">Cover</span>
                    )}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition flex flex-col items-center justify-center gap-1">
                      <div className="flex gap-1">
                        {idx > 0 && (
                          <button onClick={() => moveImage(idx, -1)} className="text-white text-xs bg-black/60 rounded px-1.5 py-0.5">←</button>
                        )}
                        {idx < (form.images.length - 1) && (
                          <button onClick={() => moveImage(idx, 1)} className="text-white text-xs bg-black/60 rounded px-1.5 py-0.5">→</button>
                        )}
                      </div>
                      <button data-testid={`product-img-remove-${idx}`} onClick={() => removeImage(idx)} className="text-red-300 text-[10px] hover:text-red-100">
                        <Trash2 className="h-3 w-3 inline mr-0.5" /> Rimuovi
                      </button>
                    </div>
                  </div>
                ))}
                <button
                  data-testid="product-img-add"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="aspect-square rounded-lg border-2 border-dashed border-zinc-700 hover:border-amber-400/60 text-zinc-500 hover:text-amber-300 flex flex-col items-center justify-center gap-1 text-[10px]"
                >
                  {uploading ? "Upload…" : (<><Plus className="h-5 w-5" /> Aggiungi</>)}
                </button>
              </div>
              <div className="mb-2">
                <AiImage
                  triggerLabel="Genera immagine con AI"
                  testid="products-ai-image"
                  defaultPrompt={form.name ? `Prodotto ristorante: ${form.name}. Foto food styling, luce calda, sfondo neutro.` : ""}
                  promptHint="Descrivi il piatto (es. Kebab classico, luce calda, lifestyle)"
                  onGenerated={(url) => {
                    setForm((prev) => ({
                      ...prev,
                      images: [...(prev.images || []), url],
                      image_thumbs: [...(prev.image_thumbs || []), url],
                    }));
                    toast.success("Immagine aggiunta alla gallery");
                  }}
                />
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                data-testid="product-img-input"
                onChange={onUpload}
              />
              <p className="text-[11px] text-zinc-500">La prima immagine è la copertina · trascina o usa le frecce per riordinare.</p>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-zinc-950 border border-zinc-800">
              <div><div className="text-sm font-medium">Attivo al negozio</div><div className="text-xs text-zinc-500">Disattiva per nasconderlo dal POS</div></div>
              <Switch data-testid="product-form-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            </div>
            {branches.length > 0 && (
              <div className="p-3 rounded-lg bg-zinc-950 border border-zinc-800">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-2 mb-2">
                  <Store className="h-3 w-3 text-amber-300" /> Disponibile nelle sedi
                </div>
                <p className="text-[11px] text-zinc-500 mb-2">
                  Nessuna sede selezionata = prodotto disponibile in <b>tutte</b> le sedi.
                </p>
                <div className="grid grid-cols-2 gap-2" data-testid="product-form-branches">
                  {branches.map((b) => {
                    const on = (form.branch_ids || []).includes(b.id);
                    return (
                      <button
                        key={b.id}
                        type="button"
                        data-testid={`product-form-branch-${b.id}`}
                        onClick={() => setForm((prev) => ({
                          ...prev,
                          branch_ids: on
                            ? (prev.branch_ids || []).filter((x) => x !== b.id)
                            : [...(prev.branch_ids || []), b.id],
                        }))}
                        className={`px-3 py-2 rounded-lg border text-left text-sm flex items-center gap-2 ${on ? "border-amber-500 bg-amber-500/10 text-amber-100" : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:border-zinc-700"}`}
                      >
                        <span className={`h-4 w-4 rounded border flex items-center justify-center flex-shrink-0 ${on ? "bg-amber-500/20 border-amber-500" : "border-zinc-700"}`}>
                          {on && <Check className="h-3 w-3" />}
                        </span>
                        <span className="truncate">{b.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <Button data-testid="product-form-submit" onClick={submit} className="w-full bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]">
              {editing ? "Salva" : "Crea prodotto"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Customers dialog */}
      <Dialog open={!!customersFor} onOpenChange={(o) => !o && setCustomersFor(null)}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Clienti che hanno acquistato: {customersFor?.name}</DialogTitle></DialogHeader>
          <div className="text-xs text-zinc-500 mb-2">Ultimi 180 giorni · {productCustomers.length} clienti</div>
          <div className="space-y-1 max-h-96 overflow-y-auto">
            {productCustomers.length === 0 && <div className="text-sm text-zinc-500">Nessun cliente nel periodo.</div>}
            {productCustomers.map((c) => (
              <div key={c.customer_id} className="p-2 rounded-md border border-zinc-900 flex justify-between items-center">
                <div>
                  <div className="text-sm font-medium">{c.name}</div>
                  <div className="text-xs text-zinc-500 font-mono">{c.card_number} · {c.phone}</div>
                </div>
                <div className="text-right text-xs">
                  <div className="font-mono text-emerald-300">€ {c.revenue.toFixed(2)}</div>
                  <div className="text-zinc-500">{c.units} pz · {c.orders} ordini</div>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      {/* Gallery Lightbox */}
      <GalleryLightbox item={galleryFor} onClose={() => setGalleryFor(null)} />
    </div>
  );
}

/* Small reusable lightbox that shows a set of images with prev/next */
function GalleryLightbox({ item, onClose }) {
  const [idx, setIdx] = React.useState(0);
  React.useEffect(() => { setIdx(0); }, [item?.id]);
  if (!item) return null;
  const images = (item.images && item.images.length) ? item.images : (item.image_url ? [item.image_url] : []);
  if (!images.length) return null;
  const url = images[idx];
  const prev = (e) => { e?.stopPropagation(); setIdx((i) => (i - 1 + images.length) % images.length); };
  const next = (e) => { e?.stopPropagation(); setIdx((i) => (i + 1) % images.length); };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-w-3xl p-0 overflow-hidden" data-testid="product-gallery-lightbox">
        <DialogHeader className="p-4 pb-2"><DialogTitle>{item.name}</DialogTitle></DialogHeader>
        <div className="relative bg-black">
          <img
            src={resolveImg(url)}
            alt={item.name}
            className="w-full max-h-[70vh] object-contain bg-black"
            data-testid={`product-gallery-img-${idx}`}
          />
          {images.length > 1 && (
            <>
              <button onClick={prev} className="absolute left-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/70 hover:bg-black text-white flex items-center justify-center" data-testid="product-gallery-prev">‹</button>
              <button onClick={next} className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/70 hover:bg-black text-white flex items-center justify-center" data-testid="product-gallery-next">›</button>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1">
                {images.map((_, i) => (
                  <span key={i} className={`h-1.5 w-1.5 rounded-full ${i === idx ? "bg-amber-300" : "bg-zinc-500/60"}`} />
                ))}
              </div>
            </>
          )}
        </div>
        <div className="p-3 flex gap-2 overflow-x-auto bg-zinc-950 border-t border-zinc-800">
          {images.map((u, i) => (
            <button key={i} onClick={() => setIdx(i)} className={`h-14 w-14 rounded-md overflow-hidden border ${i === idx ? "border-amber-400" : "border-zinc-800"} shrink-0`}>
              <img src={resolveImg(u)} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}